import React, { memo, useState, useCallback, useMemo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import { FormFieldSetting, GhlCustomFieldResponse } from '../types';

import classes from './formFieldSettings.module.css';

interface FormFieldSettingsProps {
  locationId: string;
  ssoToken: string;
  customFields: GhlCustomFieldResponse[];
  savedSettings: FormFieldSetting[];
  onSave: (settings: FormFieldSetting[]) => void;
}

const HARDCODED_FIELD_KEYS = [
  'contact.type_of_loss',
  'contact.class',
  'contact.industry',
  'contact.notes',
  'contact.referred_by',
  'contact.referred_by_id',
  'opportunity.type_of_loss',
  'opportunity.class',
  'opportunity.notes',
  'opportunity.referred_by',
  'opportunity.referred_by_id',
  'opportunity.lead_value',
  'opportunity.office',
];

function FormFieldSettingsComponent({
  locationId,
  ssoToken,
  customFields,
  savedSettings,
  onSave,
}: FormFieldSettingsProps) {
  const [settings, setSettings] = useState<FormFieldSetting[]>(savedSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const availableFields = useMemo(() => {
    return customFields.filter((field) => !HARDCODED_FIELD_KEYS.includes(field.fieldKey));
  }, [customFields]);

  const addedFieldKeys = useMemo(() => new Set(settings.map((s) => s.fieldKey)), [settings]);

  const handleAddField = useCallback((field: GhlCustomFieldResponse) => {
    const newSetting: FormFieldSetting = {
      fieldKey: field.fieldKey,
      fieldId: field.id,
      label: field.name,
      dataType: field.dataType,
      picklistOptions: field.picklistOptions || [],
      applyTo: 'both',
    };
    setSettings((prev) => [...prev, newSetting]);
  }, []);

  const handleRemoveField = useCallback((fieldKey: string) => {
    setSettings((prev) => prev.filter((s) => s.fieldKey !== fieldKey));
  }, []);

  const handleUpdateSetting = useCallback((fieldKey: string, updates: Partial<FormFieldSetting>) => {
    setSettings((prev) =>
      prev.map((s) => (s.fieldKey === fieldKey ? { ...s, ...updates } : s))
    );
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await Api.put('/ghl/embed/form-field-config', { fields: settings }, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      setSaveMessage('Settings saved successfully!');
      onSave(settings);

      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  }, [settings, ssoToken, onSave]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h5>Form Field Settings</h5>
        <p className={classes.description}>
          Add or remove custom fields from the New Lead and Existing Lead forms.
        </p>
      </div>

      {saveMessage && (
        <div className={`alert ${saveMessage.includes('Failed') ? 'alert-danger' : 'alert-success'} mb-3`}>
          {saveMessage}
        </div>
      )}

      <div className={classes.content}>
        <div className={classes.leftPanel}>
          <h6>Available Fields</h6>
          <p className={classes.hint}>Click a field to add it to the form</p>
          <div className={classes.fieldList}>
            {availableFields.length === 0 ? (
              <p className={classes.emptyState}>No additional custom fields available</p>
            ) : (
              availableFields.map((field) => (
                <div key={field.id} className={classes.fieldItem}>
                  <button
                    type="button"
                    className={classes.addButton}
                    onClick={() => handleAddField(field)}
                    disabled={addedFieldKeys.has(field.fieldKey)}
                  >
                    <span className={classes.fieldName}>{field.name}</span>
                    <span className={classes.fieldKey}>{field.fieldKey}</span>
                    <span className={classes.fieldType}>{field.dataType}</span>
                    {addedFieldKeys.has(field.fieldKey) && <span className={classes.addedBadge}>Added</span>}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={classes.rightPanel}>
          <h6>Fields on Form</h6>
          <p className={classes.hint}>Configure how each field appears</p>
          <div className={classes.fieldList}>
            {settings.length === 0 ? (
              <p className={classes.emptyState}>No fields added yet</p>
            ) : (
              settings.map((setting) => (
                <div key={setting.fieldKey} className={classes.configuredField}>
                  <div className={classes.configuredFieldHeader}>
                    <span className={classes.fieldName}>{setting.label}</span>
                    <button
                      type="button"
                      className={classes.removeButton}
                      onClick={() => handleRemoveField(setting.fieldKey)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className={classes.configuredFieldBody}>
                    <div className={classes.formGroup}>
                      <label>Label</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={setting.label}
                        onChange={(e) => handleUpdateSetting(setting.fieldKey, { label: e.target.value })}
                      />
                    </div>
                    <div className={classes.formGroup}>
                      <label>Show On</label>
                      <select
                        className="form-select form-select-sm"
                        value={setting.applyTo}
                        onChange={(e) => handleUpdateSetting(setting.fieldKey, { applyTo: e.target.value as any })}
                      >
                        <option value="new">New Lead Form</option>
                        <option value="existing">Existing Lead Form</option>
                        <option value="both">Both Forms</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

const FormFieldSettingsMemo = memo(FormFieldSettingsComponent, areEqual);

export { FormFieldSettingsMemo as FormFieldSettings };